import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { apiFetch, apiClient } from '../lib/api';

const CACHE_KEY = 'mig_watchlist';

const KNOWN_NAMES = {
  SNDK: 'SanDisk Corporation',   MU:   'Micron Technology',
  INTC: 'Intel Corporation',     NVDA: 'Nvidia Corporation',
  META: 'Meta Platforms',        LRCX: 'Lam Research',
  ALAB: 'Astera Labs',           STX:  'Seagate Technology',
  AAPL: 'Apple Inc.',            MSFT: 'Microsoft',
  GOOGL: 'Alphabet Inc.',        AMZN: 'Amazon',
  TSLA: 'Tesla',                 AMD:  'Advanced Micro Devices',
  TSMC: 'Taiwan Semiconductor',  ASML: 'ASML Holding',
  WDC:  'Western Digital',       AMAT: 'Applied Materials',
  QCOM: 'Qualcomm',              TXN:  'Texas Instruments',
  AVGO: 'Broadcom',              MRVL: 'Marvell Technology',
};

const FALLBACK = [
  { sym: 'SNDK', name: 'SanDisk Corporation' },
  { sym: 'MU',   name: 'Micron Technology' },
  { sym: 'INTC', name: 'Intel Corporation' },
  { sym: 'NVDA', name: 'Nvidia Corporation' },
  { sym: 'META', name: 'Meta Platforms' },
  { sym: 'LRCX', name: 'Lam Research' },
  { sym: 'ALAB', name: 'Astera Labs' },
  { sym: 'STX',  name: 'Seagate Technology' },
  { sym: 'AMD',  name: 'Advanced Micro Devices' },
  { sym: 'MSFT', name: 'Microsoft' },
];

function loadCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function saveCache(stocks) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(stocks)); } catch {}
}

const WatchlistContext = createContext(null);

export function WatchlistProvider({ children }) {
  const { user } = useAuth();
  const [stocks, setStocksRaw] = useState(() => loadCache() ?? FALLBACK);

  function setStocks(update) {
    setStocksRaw(prev => {
      const next = typeof update === 'function' ? update(prev) : update;
      saveCache(next);
      return next;
    });
  }

  // Sync from API whenever the user signs in
  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    async function sync() {
      try {
        const token = await user.getIdToken();
        const res = await apiFetch('/api/watchlist', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        let list = (data.stocks ?? []).map(s => ({
          sym:  s.sym ?? s.symbol,
          name: s.name ?? KNOWN_NAMES[s.sym ?? s.symbol] ?? (s.sym ?? s.symbol),
        }));

        if (list.length < 10) {
          const tickerRes = await apiClient.get('/api/market/ticker');
          const tickers = tickerRes.data?.tickers ?? [];
          const quotes  = tickerRes.data?.quotes  ?? {};
          const existing = new Set(list.map(s => s.sym));
          const topFill = [...tickers]
            .filter(sym => !existing.has(sym))
            .sort((a, b) => (quotes[b]?.change_pct ?? 0) - (quotes[a]?.change_pct ?? 0));
          for (const sym of topFill) {
            if (list.length >= 10) break;
            list.push({ sym, name: KNOWN_NAMES[sym] ?? sym });
          }
        }

        if (!cancelled && list.length > 0) setStocks(list);
      } catch {}
    }

    sync();
    return () => { cancelled = true; };
  }, [user]);

  async function addStock(sym, name) {
    setStocks(prev => prev.some(s => s.sym === sym) ? prev : [...prev, { sym, name }]);
    if (!user) return;
    try {
      const token = await user.getIdToken();
      await apiFetch('/api/watchlist', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticker: sym }),
      });
    } catch {}
  }

  async function removeStock(sym) {
    setStocks(prev => prev.filter(s => s.sym !== sym));
    if (!user) return;
    try {
      const token = await user.getIdToken();
      await apiFetch(`/api/watchlist/${sym}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {}
  }

  async function reorderStocks(syms) {
    setStocks(prev => {
      const map = Object.fromEntries(prev.map(s => [s.sym, s]));
      return syms.map(sym => map[sym]).filter(Boolean);
    });
    if (!user) return;
    try {
      const token = await user.getIdToken();
      await apiFetch('/api/watchlist/reorder', {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ tickers: syms }),
      });
    } catch {}
  }

  return (
    <WatchlistContext.Provider value={{ stocks, addStock, removeStock, reorderStocks }}>
      {children}
    </WatchlistContext.Provider>
  );
}

export function useWatchlist() {
  const ctx = useContext(WatchlistContext);
  if (!ctx) throw new Error('useWatchlist must be used inside WatchlistProvider');
  return ctx;
}
